/**
 * Comments service layer.
 * Abstracts all Firestore operations for post comments.
 * Comments are stored as subcollections under posts: posts/{postId}/comments/{commentId}
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  serverTimestamp,
  writeBatch,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const POSTS_COLLECTION = 'posts';
const COMMENTS_SUBCOLLECTION = 'comments';

/**
 * Serialize a Firestore comment document snapshot into a plain object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object} A plain comment object.
 */
const serializeComment = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    text: data.text || '',
    authorId: data.authorId || '',
    authorName: data.authorName || '',
    authorUsername: data.authorUsername || '',
    authorAvatarUrl: data.authorAvatarUrl || null,
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : data.updatedAt || null,
    _docSnapshot: docSnap,
  };
};

/**
 * Add a comment to a post.
 * Uses a batch write to create the comment subdocument and increment commentsCount atomically.
 * @param {string} postId - The post to comment on.
 * @param {Object} params
 * @param {string} params.text - The comment text.
 * @param {Object} params.author - The comment author's profile data.
 * @param {string} params.author.uid - The author's user ID.
 * @param {string} params.author.displayName - The author's display name.
 * @param {string} params.author.username - The author's username.
 * @param {string} [params.author.avatarUrl] - The author's avatar URL.
 * @returns {Promise<Object>} The created comment object.
 */
export const addComment = async (postId, { text, author }) => {
  try {
    const commentsRef = collection(db, POSTS_COLLECTION, postId, COMMENTS_SUBCOLLECTION);
    const commentDoc = doc(commentsRef);

    const commentData = {
      text: text.trim(),
      authorId: author.uid,
      authorName: author.displayName,
      authorUsername: author.username,
      authorAvatarUrl: author.avatarUrl || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const batch = writeBatch(db);

    // Create the comment
    batch.set(commentDoc, commentData);

    // Increment the post's commentsCount
    const postRef = doc(db, POSTS_COLLECTION, postId);
    batch.update(postRef, { commentsCount: increment(1) });

    await batch.commit();

    return {
      id: commentDoc.id,
      ...commentData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _docSnapshot: null,
    };
  } catch (error) {
    handleServiceError(error, 'Add comment', 'Failed to add comment. Please try again.');
  }
};

/**
 * Retrieve paginated comments for a post, ordered by createdAt ascending (oldest first).
 * @param {string} postId - The post ID to get comments for.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of comments per page.
 * @returns {Promise<{comments: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getComments = async (postId, lastDoc = null, pageLimit = 10) => {
  try {
    const commentsRef = collection(db, POSTS_COLLECTION, postId, COMMENTS_SUBCOLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        commentsRef,
        orderBy('createdAt', 'asc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        commentsRef,
        orderBy('createdAt', 'asc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const comments = querySnapshot.docs.map(serializeComment);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { comments, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get comments', 'Failed to load comments. Please try again.');
  }
};

/**
 * Update a comment's text content.
 * @param {string} postId - The parent post ID.
 * @param {string} commentId - The comment ID to update.
 * @param {string} text - The updated comment text.
 * @returns {Promise<Object>} The updated comment object.
 */
export const updateComment = async (postId, commentId, text) => {
  try {
    const commentRef = doc(db, POSTS_COLLECTION, postId, COMMENTS_SUBCOLLECTION, commentId);
    await updateDoc(commentRef, {
      text: text.trim(),
      updatedAt: serverTimestamp(),
    });

    const updatedSnap = await getDoc(commentRef);
    return serializeComment(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Update comment', 'Failed to update comment. Please try again.');
  }
};

/**
 * Delete a comment from a post.
 * Uses a batch write to delete the comment and decrement the commentsCount atomically.
 * @param {string} postId - The parent post ID.
 * @param {string} commentId - The comment ID to delete.
 * @returns {Promise<string>} The deleted comment ID.
 */
export const deleteComment = async (postId, commentId) => {
  try {
    const batch = writeBatch(db);

    // Delete the comment
    const commentRef = doc(db, POSTS_COLLECTION, postId, COMMENTS_SUBCOLLECTION, commentId);
    batch.delete(commentRef);

    // Decrement the post's commentsCount
    const postRef = doc(db, POSTS_COLLECTION, postId);
    batch.update(postRef, { commentsCount: increment(-1) });

    await batch.commit();

    return commentId;
  } catch (error) {
    handleServiceError(error, 'Delete comment', 'Failed to delete comment. Please try again.');
  }
};
